'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { waitForPageMedia } from '@/lib/wait-for-page-media';
import Image from 'next/image';
import { MenuSection, Section } from '@/types';
import { isVideoSource, getMediaTypeFromUrl } from '@/lib/video-utils';
import { extractFallbackImagesFromSection } from '@/lib/video-fallback-utils';
import { extractMediaUrlsFromSection } from '@/lib/section-media-utils';

interface PageMediaLoaderLayoutProps {
  children: React.ReactNode;
  menuSections?: MenuSection[];
  sections?: Section[];
}

/**
 * Detect if a section is a Hero type section
 */
function isHeroSectionType(sectionType: string | null | undefined): boolean {
  if (!sectionType) return false;
  const heroTypes = ['HERO', 'SERVICES_HERO', 'CONTACT_HERO', 'HERO_CAROUSEL'];
  return heroTypes.includes(sectionType.toUpperCase());
}

/**
 * Detect if a section has a video background
 */
function hasVideoBackground(section: Section): boolean {
  const sectionData = section.sectionData;
  if (!sectionData || typeof sectionData !== 'object') return false;
  
  const backgroundVideo = (sectionData as any).backgroundVideo;
  const backgroundMedia = (sectionData as any).backgroundMedia;
  
  // Check backgroundVideo
  if (backgroundVideo) {
    if (typeof backgroundVideo === 'string') {
      return isVideoSource(backgroundVideo);
    }
    if (typeof backgroundVideo === 'object') {
      const desktop = (backgroundVideo as any).desktop;
      const mobile = (backgroundVideo as any).mobile;
      return (desktop && isVideoSource(desktop)) || (mobile && isVideoSource(mobile));
    }
  }
  
  // Check backgroundMedia (unified field)
  if (backgroundMedia) {
    if (typeof backgroundMedia === 'string') {
      return isVideoSource(backgroundMedia);
    }
    if (typeof backgroundMedia === 'object') {
      const desktop = (backgroundMedia as any).desktop;
      const mobile = (backgroundMedia as any).mobile;
      return (desktop && isVideoSource(desktop)) || (mobile && isVideoSource(mobile));
    }
  }
  
  return false;
}

/**
 * Detect if a page has critical video sections (Hero or video background in positions 0-1)
 */
function hasCriticalVideoSections(sections: Section[] = []): boolean {
  // Check first two sections (positions 0 and 1)
  const criticalSections = sections.slice(0, 2);
  
  return criticalSections.some(section => {
    // Check if it's a Hero type section
    if (isHeroSectionType(section.type)) {
      return true;
    }
    
    // Check if it has a video background
    if (hasVideoBackground(section)) {
      return true;
    }
    
    return false;
  });
}

/**
 * Detect if a video element is in a critical section (Hero or first/second section)
 */
function isCriticalVideo(videoElement: HTMLVideoElement): boolean {
  // Check if video is in a section with id="hero"
  const heroSection = videoElement.closest('section[id="hero"]');
  if (heroSection) return true;
  
  // Check if video is in first or second section
  const allSections = Array.from(document.querySelectorAll('section'));
  if (allSections.length > 0 && allSections[0].contains(videoElement)) return true;
  if (allSections.length > 1 && allSections[1].contains(videoElement)) return true;
  
  // Check parent for hero-related classes/ids
  let parent = videoElement.parentElement;
  while (parent) {
    if (parent.id?.includes('hero') || parent.className?.includes('hero')) {
      return true;
    }
    parent = parent.parentElement;
  }
  
  return false;
}

/**
 * Preload fallback images with link tags for immediate browser preload
 */
function preloadFallbackImages(imageUrls: string[]): void {
  if (typeof window === 'undefined' || imageUrls.length === 0) return;
  
  imageUrls.forEach(url => {
    // Skip if already preloaded
    const existingLink = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`);
    if (existingLink) return;
    
    // Skip videos - only preload images
    const mediaType = getMediaTypeFromUrl(url);
    if (mediaType === 'video') return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
  });
}

/**
 * NOTE: Video preloading is handled by S3FilePreview component itself
 * based on priority props. We don't preload videos here to avoid conflicts
 * with S3FilePreview's cache checking and loading mechanism.
 * S3FilePreview uses mediaCache and has proper deduplication.
 */

/**
 * Wait for fallback images to be loaded by checking if they're in browser cache or loaded
 * CRITICAL: Must wait for ALL fallback images before showing page content
 */
async function waitForFallbackImages(imageUrls: string[], timeoutMs: number = 5000): Promise<void> {
  if (imageUrls.length === 0) {
    return Promise.resolve();
  }
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    let checkCount = 0;
    
    const checkFallbackImages = () => {
      // Check if images are loaded by creating Image objects and checking their complete state
      let loadedCount = 0;
      let totalToCheck = 0;
      
      const checkPromises: Promise<boolean>[] = imageUrls.map(url => {
        // Skip data URLs and blob URLs - they're already loaded
        if (url.startsWith('data:') || url.startsWith('blob:')) {
          return Promise.resolve(true);
        }
        
        // Skip videos
        const mediaType = getMediaTypeFromUrl(url);
        if (mediaType === 'video') {
          return Promise.resolve(true);
        }
        
        totalToCheck++;
        
        return new Promise<boolean>((resolveCheck) => {
          const img = document.createElement('img');
          let resolved = false;
          
          const resolveOnce = (loaded: boolean) => {
            if (!resolved) {
              resolved = true;
              resolveCheck(loaded);
            }
          };
          
          img.onload = () => resolveOnce(true);
          img.onerror = () => resolveOnce(true); // Resolve on error too - don't block forever
          
          // Set src to trigger load check
          img.src = url;
          
          // If already cached, onload fires immediately
          if (img.complete) {
            resolveOnce(true);
          }
          
          // Timeout for individual image check - longer on mobile
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
          const individualTimeout = isMobile ? 3000 : 2000; // 3s mobile, 2s desktop
          setTimeout(() => resolveOnce(false), individualTimeout);
        });
      });
      
      Promise.all(checkPromises).then(results => {
        loadedCount = results.filter(r => r === true).length;
        
        const elapsed = Date.now() - startTime;
        const allLoaded = loadedCount === totalToCheck || totalToCheck === 0;
        
        if (allLoaded) {
          // All images loaded or no images to check
          resolve();
        } else if (elapsed >= timeoutMs) {
          // Timeout - don't block forever
          if (process.env.NODE_ENV === 'development') {
            console.warn('[PageMediaLoaderLayout] Timeout waiting for fallback images', {
              total: totalToCheck,
              loaded: loadedCount
            });
          }
          resolve();
        } else {
          // Check again after a short delay
          const checkInterval = checkCount < 10 ? 50 : 100;
          checkCount++;
          setTimeout(checkFallbackImages, checkInterval);
        }
      }).catch(() => {
        // On error, resolve anyway to not block forever
        resolve();
      });
    };
    
    // Start checking immediately
    checkFallbackImages();
  });
}

/**
 * Wait for videos to be ready by preloading them and checking readyState
 * CRITICAL: Videos must have HAVE_CURRENT_DATA (readyState >= 2) before showing page
 * This ensures smooth transition from fallback image to video with no black background
 */
async function waitForVideos(videoUrls: string[], timeoutMs: number = 5000): Promise<void> {
  if (videoUrls.length === 0) {
    return Promise.resolve();
  }
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    let checkCount = 0;
    
    const checkVideos = () => {
      // Check if videos are loaded by creating Video elements and checking their readyState
      let readyCount = 0;
      let totalToCheck = 0;
      
      const checkPromises: Promise<boolean>[] = videoUrls.map(url => {
        // Skip if not a video
        if (!isVideoSource(url)) {
          return Promise.resolve(true);
        }
        
        totalToCheck++;
        
        return new Promise<boolean>((resolveCheck) => {
          const video = document.createElement('video');
          let resolved = false;
          
          const resolveOnce = (ready: boolean) => {
            if (!resolved) {
              resolved = true;
              resolveCheck(ready);
            }
          };
          
          // Check for HAVE_CURRENT_DATA (readyState >= 2) - enough to show first frame
          const checkReadyState = () => {
            if (video.readyState >= 2) {
              resolveOnce(true);
            }
          };
          
          video.addEventListener('loadeddata', checkReadyState, { once: true });
          video.addEventListener('canplay', checkReadyState, { once: true });
          video.addEventListener('error', () => resolveOnce(true)); // Resolve on error too - don't block forever
          
          // Set src to trigger load
          video.preload = 'metadata';
          video.src = url;
          
          // If already has data, resolve immediately
          if (video.readyState >= 2) {
            resolveOnce(true);
          }
          
          // Timeout for individual video check
          setTimeout(() => resolveOnce(false), 2000);
        });
      });
      
      Promise.all(checkPromises).then(results => {
        readyCount = results.filter(r => r === true).length;
        
        const elapsed = Date.now() - startTime;
        const allReady = readyCount === totalToCheck || totalToCheck === 0;
        
        if (allReady) {
          // All videos ready or no videos to check
          resolve();
        } else if (elapsed >= timeoutMs) {
          // Timeout - don't block forever
          if (process.env.NODE_ENV === 'development') {
            console.warn('[PageMediaLoaderLayout] Timeout waiting for videos', {
              total: totalToCheck,
              ready: readyCount
            });
          }
          resolve();
        } else {
          // Check again after a short delay
          const checkInterval = checkCount < 10 ? 100 : 200;
          checkCount++;
          setTimeout(checkVideos, checkInterval);
        }
      }).catch(() => {
        // On error, resolve anyway to not block forever
        resolve();
      });
    };
    
    // Start checking immediately
    checkVideos();
  });
}

/**
 * Full-page loader layout that shows while critical media (especially autoplay hero videos) is loading.
 * Prevents black backgrounds from appearing before videos are ready to play.
 * Now works on all pages that have Hero sections or sections with video backgrounds in positions 0-1.
 */
export default function PageMediaLoaderLayout({ children, menuSections = [], sections = [] }: PageMediaLoaderLayoutProps) {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showChildren, setShowChildren] = useState(false);
  const hasStartedLoading = useRef(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentPathnameRef = useRef<string | null>(null);

  // Detect if this page has critical video sections (Hero or video background in positions 0-1)
  const hasCriticalVideos = useMemo(() => {
    return hasCriticalVideoSections(sections);
  }, [sections]);

  // Extract ALL fallback images from all sections for preloading
  // CRITICAL: Hero sections get highest priority - extract them first
  const { heroFallbackImageUrls, otherFallbackImageUrls } = useMemo(() => {
    const heroUrls: string[] = [];
    const otherUrls: string[] = [];
    const seen = new Set<string>();
    
    sections.forEach((section, index) => {
      const isHero = isHeroSectionType(section.type) || index === 0;
      
      // Extract for both mobile and desktop to get all possible fallback images
      const fallbackImagesMobile = extractFallbackImagesFromSection(section, true);
      const fallbackImagesDesktop = extractFallbackImagesFromSection(section, false);
      const allFallbackImages = [...fallbackImagesMobile, ...fallbackImagesDesktop];
      
      allFallbackImages.forEach(img => {
        if (img.src && !seen.has(img.src)) {
          seen.add(img.src);
          if (isHero) {
            heroUrls.push(img.src);
          } else {
            otherUrls.push(img.src);
          }
        }
      });
    });
    
    return { heroFallbackImageUrls: heroUrls, otherFallbackImageUrls: otherUrls };
  }, [sections]);
  
  // Combine with Hero images first for priority loading
  const allFallbackImageUrls = useMemo(() => {
    return [...heroFallbackImageUrls, ...otherFallbackImageUrls];
  }, [heroFallbackImageUrls, otherFallbackImageUrls]);

  // Extract ALL video URLs from all sections for preloading
  // CRITICAL: Hero section videos get highest priority - extract them first
  const { heroVideoUrls, otherVideoUrls } = useMemo(() => {
    const heroUrls: string[] = [];
    const otherUrls: string[] = [];
    const seen = new Set<string>();
    
    sections.forEach((section, index) => {
      const isHero = isHeroSectionType(section.type) || index === 0;
      const mediaUrls = extractMediaUrlsFromSection(section);
      
      mediaUrls.forEach(url => {
        // Only include videos
        if (isVideoSource(url) && !seen.has(url)) {
          seen.add(url);
          if (isHero) {
            heroUrls.push(url);
          } else {
            otherUrls.push(url);
          }
        }
      });
    });
    
    return { heroVideoUrls: heroUrls, otherVideoUrls: otherUrls };
  }, [sections]);
  
  // Combine with Hero videos first for priority loading
  const allVideoUrls = useMemo(() => {
    return [...heroVideoUrls, ...otherVideoUrls];
  }, [heroVideoUrls, otherVideoUrls]);

  // NOTE: Fallback images are now preloaded INSIDE checkMedia() before waiting
  // This ensures preloading happens before any rendering, improving loading order
  // The preload happens synchronously before waitForFallbackImages() is called

  // NOTE: Videos are preloaded by S3FilePreview component based on priority
  // No need to preload here - S3FilePreview handles it with proper cache checking
  // Removing video preloading to avoid conflicts with S3FilePreview's loading mechanism

  // Extract header logo from menu sections prop (from database) - no API call needed
  const { logoUrl, logoAlt } = useMemo(() => {
    // Find header menu section from props (already loaded from database)
    const header = menuSections.find((ms: MenuSection) => ms.type === 'HEADER' && ms.isActive);
    
    if (header?.content?.logo) {
      const logo = header.content.logo as string;
      const alt = (header.content.logoAlt as string) || 'Logo';
      return {
        logoUrl: logo.trim(),
        logoAlt: alt,
      };
    }
    
    return {
      logoUrl: null,
      logoAlt: 'Logo',
    };
  }, [menuSections]);

  useEffect(() => {
    // Reset loading state when pathname changes (new page navigation)
    if (currentPathnameRef.current !== pathname) {
      currentPathnameRef.current = pathname;
      hasStartedLoading.current = false;
      setIsLoading(true);
      setIsFadingOut(false);
      setShowChildren(false);
      
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }

    // Prevent multiple initializations for the same pathname
    if (hasStartedLoading.current) return;
    hasStartedLoading.current = true;

    const startTime = Date.now();

    // CRITICAL: Set an absolute maximum timeout to prevent infinite loading
    // This ensures the loader ALWAYS hides, even if media loading fails
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const absoluteMaxTimeout = isMobile ? 12000 : 8000; // 12s mobile, 8s desktop
    
    loadingTimeoutRef.current = setTimeout(() => {
      // Force hide loader after absolute maximum timeout
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PageMediaLoaderLayout] Absolute timeout reached, forcing loader to hide');
      }
      setShowChildren(true);
      setIsFadingOut(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
    }, absoluteMaxTimeout);

    const checkMedia = async () => {
      try {
        // CRITICAL: MUST wait for ALL fallback images to load before showing page
        // This ensures the page always has visible content (LCP element) with images already loaded
        // On mobile, use longer timeout due to slower connections
        const timeoutMs = isMobile ? 8000 : 5000; // 8 seconds on mobile, 5 on desktop
        
        // CRITICAL: Preload Hero fallback images FIRST with highest priority
        // These must load before anything else to prevent black backgrounds
        if (heroFallbackImageUrls.length > 0) {
          preloadFallbackImages(heroFallbackImageUrls);
          // Wait for Hero fallback images to load first (shorter timeout for Hero)
          await waitForFallbackImages(heroFallbackImageUrls, isMobile ? 5000 : 3000);
        }
        
        // Then preload other fallback images
        if (otherFallbackImageUrls.length > 0) {
          preloadFallbackImages(otherFallbackImageUrls);
        }
        
        // Wait for ALL fallback images to load (but Hero images already loaded)
        await waitForFallbackImages(allFallbackImageUrls, timeoutMs);
        
        // CRITICAL: Preload Hero videos immediately (they need to start loading ASAP)
        // On mobile, Hero videos must load quickly to prevent black background gap
        if (heroVideoUrls.length > 0) {
          // Preload Hero videos with link tags for immediate browser preload
          heroVideoUrls.forEach(url => {
            const existingLink = document.querySelector(`link[rel="preload"][as="video"][href="${url}"]`);
            if (!existingLink) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'fetch'; // Use 'fetch' instead of 'video' for better browser support
              link.href = url;
              link.crossOrigin = 'anonymous'; // Fix credentials mode mismatch
              link.setAttribute('fetchpriority', 'high');
              document.head.appendChild(link);
            }
          });
          
          // Also start loading Hero videos immediately
          waitForVideos(heroVideoUrls, isMobile ? 10000 : 5000).catch(() => {
            // Silently handle errors
          });
        }
        
        // CRITICAL: Only show children AFTER Hero fallback images are confirmed loaded
        // This prevents black backgrounds from appearing
        setShowChildren(true);
        
        // Start other video loading in background (non-blocking)
        // Don't wait for videos - they'll load while fallback images are visible
        if (otherVideoUrls.length > 0) {
          waitForVideos(otherVideoUrls, 2000).catch(() => {
            // Silently handle errors - videos will load in background
          });
        }
        
        // Minimum display time to ensure smooth transition
        const elapsed = Date.now() - startTime;
        const minDisplayTime = 100;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        // Start fade-out animation
        setIsFadingOut(true);

        // Fade-out animation - 150ms for smooth transition
        await new Promise(resolve => setTimeout(resolve, 150));

        // Hide loader completely
        setIsLoading(false);
        
        // Clear the absolute timeout since we completed successfully
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      } catch (error) {
        // On error, still show children and hide loader
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PageMediaLoaderLayout] Error waiting for media:', error);
        }
        
        // Show children even on error
        setShowChildren(true);
        
        const elapsed = Date.now() - startTime;
        const minDisplayTime = 100;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        setIsFadingOut(true);
        await new Promise(resolve => setTimeout(resolve, 150));
        setIsLoading(false);
        
        // Clear the absolute timeout
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      }
    };

    // Start checking immediately on next frame
    const startCheck = () => {
      requestAnimationFrame(() => {
        checkMedia();
      });
    };

    startCheck();

    // Cleanup function to clear timeout on unmount or pathname change
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [pathname, hasCriticalVideos, allFallbackImageUrls, allVideoUrls]);

  return (
    <>
      {/* Only render children after fallback images are loaded */}
      {showChildren && children}

      {/* Full-page loader overlay - only visible when loading */}
      {/* z-index 15000 to be above Header (12000) and hero content including CTA buttons (14000) */}
      {/* Hero text content and CTA buttons (14000) will be below this overlay */}
      {isLoading && (
        <>
          {/* Heartbeat animation styles */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes heartbeat {
                0% {
                  transform: scale(1);
                }
                14% {
                  transform: scale(1.15);
                }
                28% {
                  transform: scale(1);
                }
                42% {
                  transform: scale(1.15);
                }
                70% {
                  transform: scale(1);
                }
                100% {
                  transform: scale(1);
                }
              }
              .page-loader-heartbeat {
                animation: heartbeat 1.5s ease-in-out infinite;
              }
              /* Ensure critical section text content is visible above loader overlay */
              /* Target hero titles and descriptions in hero sections and first/second sections */
              section[id="hero"] h1.hero-title-responsive,
              section[id="hero"] h2.hero-title-responsive,
              section[id="hero"] .hero-title-responsive,
              section[id="hero"] .hero-subtitle-responsive,
              section[id="hero"] .hero-description-responsive,
              section[id="hero"] [class*="hero-title"],
              section[id="hero"] [class*="hero-description"],
              section:first-of-type h1.hero-title-responsive,
              section:first-of-type h2.hero-title-responsive,
              section:first-of-type .hero-title-responsive,
              section:first-of-type .hero-subtitle-responsive,
              section:first-of-type .hero-description-responsive,
              section:first-of-type [class*="hero-title"],
              section:first-of-type [class*="hero-description"],
              section:nth-of-type(2) h1.hero-title-responsive,
              section:nth-of-type(2) h2.hero-title-responsive,
              section:nth-of-type(2) .hero-title-responsive,
              section:nth-of-type(2) .hero-subtitle-responsive,
              section:nth-of-type(2) .hero-description-responsive,
              section:nth-of-type(2) [class*="hero-title"],
              section:nth-of-type(2) [class*="hero-description"],
              /* Also target content containers in critical sections */
              section[id="hero"] .container,
              section[id="hero"] [class*="container"],
              section:first-of-type .container,
              section:first-of-type [class*="container"],
              section:nth-of-type(2) .container,
              section:nth-of-type(2) [class*="container"] {
                position: relative;
                z-index: 14000 !important; /* Below loader overlay (15000) */
              }
              /* Prevent gray flash - ensure critical section backgrounds match loader */
              section[id="hero"],
              section:first-of-type,
              section:nth-of-type(2) {
                background-color: #ffffff !important;
                min-height: 100vh;
              }
              /* Ensure main content area has white background during transition */
              main {
                background-color: #ffffff !important;
              }
            `
          }} />
          <div
            className={`fixed inset-0 z-[15000] flex items-center justify-center transition-opacity duration-150 ${
              isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            style={{
              backgroundColor: '#ffffff', // White background
              pointerEvents: isFadingOut ? 'none' : 'auto', // Disable pointer events during fade-out so touches pass through
            }}
          >
            <div className="relative flex flex-col items-center justify-center">
              {logoUrl ? (
                /* Show menu section logo if available */
                <div 
                  className="relative page-loader-heartbeat" 
                  style={{ maxWidth: '200px', maxHeight: '80px' }}
                >
                  <Image
                    src={logoUrl}
                    alt={logoAlt}
                    width={200}
                    height={80}
                    className="object-contain"
                    priority
                    unoptimized
                  />
                </div>
              ) : (
                /* Fallback: Spinning loader if no logo */
                <div
                  className="animate-spin rounded-full border-4 border-gray-300 border-t-gray-600"
                  style={{
                    width: '56px',
                    height: '56px',
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

